import type {
  DocEntityHandle,
  FloorBayEdgeHandle,
  FloorEdgeHandle,
  FloorOpeningEdgeHandle,
  FloorSplitLineEdgeHandle,
  FoundationEdgeHandle,
  FreeformWallEdgeHandle,
  RoofEdgeHandle,
  RoomEdgeHandle,
} from "building-types/handles/docEntityHandleTypes";
import { assertIsExactly } from "common/utils/assertOfType";
import { assertIsDocEntityHandleOfType } from "./docEntityHandleTools";

export function assertIsFoundationEdgeHandle(handle: DocEntityHandle): asserts handle is FoundationEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "FOUNDATION");
  assertIsExactly(handle.shapeId, "outline");
  assertIsExactly(typeof handle.edgeId, "number");
}

export function isFoundationEdgeHandle(handle: DocEntityHandle): handle is FoundationEdgeHandle {
  return handle.type === "EDGE" && handle.parent.type === "FOUNDATION" && handle.shapeId === "outline";
}

export function assertIsFloorOpeningEdgeHandle(handle: DocEntityHandle): asserts handle is FloorOpeningEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "FLOOR");
  assertIsExactly(handle.shapeId, "opening");
  assertIsExactly(typeof handle.edgeId, "number");
  assertIsExactly(typeof (handle as { openingIndex?: number }).openingIndex, "number");
}

export function isFloorOpeningEdgeHandle(handle: DocEntityHandle): handle is FloorOpeningEdgeHandle {
  return handle.type === "EDGE" && handle.parent.type === "FLOOR" && "openingIndex" in handle;
}

export function assertIsFloorEdgeHandle(handle: DocEntityHandle): asserts handle is FloorEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "FLOOR");
  assertIsExactly(handle.shapeId, "outline");
  assertIsExactly(typeof handle.edgeId, "number");
  assertIsExactly(typeof (handle as { perimeterIndex?: number }).perimeterIndex, "number");
}

export function isFloorEdgeHandle(handle: DocEntityHandle): handle is FloorEdgeHandle {
  return (
    handle.type === "EDGE" && handle.parent.type === "FLOOR" && "shapeId" in handle && handle.shapeId === "outline"
  );
}

export function assertIsRoomEdgeHandle(handle: DocEntityHandle): asserts handle is RoomEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "ROOM");
  assertIsExactly(handle.shapeId, "outline");
  assertIsExactly(typeof handle.edgeId, "number");
}

export function isRoomEdgeHandle(handle: DocEntityHandle): handle is RoomEdgeHandle {
  return (
    handle.type === "EDGE" &&
    handle.parent.type === "ROOM" &&
    "shapeId" in handle &&
    (handle as { shapeId?: string }).shapeId === "outline"
  );
}

export function assertIsRoofEdgeHandle(handle: DocEntityHandle): asserts handle is RoofEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "ROOF");
  assertIsExactly(handle.shapeId, "outline");
  assertIsExactly(typeof handle.edgeId, "number");
}

export function isRoofEdgeHandle(handle: DocEntityHandle): handle is RoofEdgeHandle {
  return handle.type === "EDGE" && handle.parent.type === "ROOF" && handle.shapeId === "outline";
}

export function assertIsFreeformWallEdgeHandle(handle: DocEntityHandle): asserts handle is FreeformWallEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "FREEFORM_WALL");
  assertIsExactly(handle.shapeId, "path");
  assertIsExactly(typeof handle.edgeId, "number");
}

export function isFreeformWallEdgeHandle(handle: DocEntityHandle): handle is FreeformWallEdgeHandle {
  return (
    handle.type === "EDGE" &&
    handle.parent.type === "FREEFORM_WALL" &&
    "shapeId" in handle &&
    (handle as { shapeId?: string }).shapeId === "path"
  );
}

export function docEntityEdgeHandlesHaveSameStructure(a: DocEntityHandle, b: DocEntityHandle): boolean {
  if (a.type !== "EDGE" || b.type !== "EDGE") {
    return false;
  }

  if (a.parent.type !== b.parent.type) {
    return false;
  }

  if (a.shapeId !== b.shapeId) {
    return false;
  }

  const aHasOpeningIndex = "openingIndex" in a;
  const bHasOpeningIndex = "openingIndex" in b;
  if (aHasOpeningIndex !== bHasOpeningIndex) {
    return false;
  }

  const aHasPerimeterIndex = "perimeterIndex" in a;
  const bHasPerimeterIndex = "perimeterIndex" in b;
  if (aHasPerimeterIndex !== bHasPerimeterIndex) {
    return false;
  }

  return true;
}

export function assertIsFloorSplitLineEdgeHandle(handle: DocEntityHandle): asserts handle is FloorSplitLineEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "FLOOR_SPLIT_LINE");
  assertIsExactly(handle.shapeId, "splitLine");
  assertIsExactly(typeof handle.edgeId, "number");
}

export function assertIsFloorBayEdgeHandle(handle: DocEntityHandle): asserts handle is FloorBayEdgeHandle {
  assertIsDocEntityHandleOfType(handle, "EDGE");
  assertIsDocEntityHandleOfType(handle.parent, "FLOOR_BAY");
  assertIsExactly(handle.shapeId, "outline");
  assertIsExactly(typeof handle.edgeId, "number");
  assertIsExactly(typeof (handle as { stableEdgeId?: string }).stableEdgeId, "string");
}

export function isFloorBayEdgeHandle(handle: DocEntityHandle): handle is FloorBayEdgeHandle {
  return (
    handle.type === "EDGE" &&
    handle.parent.type === "FLOOR_BAY" &&
    "shapeId" in handle &&
    handle.shapeId === "outline" &&
    "stableEdgeId" in handle &&
    typeof handle.stableEdgeId === "string"
  );
}

export function isFloorSplitLineEdgeHandle(handle: DocEntityHandle): handle is FloorSplitLineEdgeHandle {
  return (
    handle.type === "EDGE" &&
    handle.parent.type === "FLOOR_SPLIT_LINE" &&
    "shapeId" in handle &&
    handle.shapeId === "splitLine"
  );
}
